/**
 * Trainer Quiz Scores panel — live + completed (Figma TrainerQuizScoresScreen).
 *
 * Tabs:
 *   Overview  — avg, submitted, confidence, score distribution
 *   Questions — options + % of students who chose each
 *   Students  — roster + per-student answer drill-down
 */

import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, ChevronRight, Clock, Moon, Star, Sun } from "lucide-react";
import type { ActiveLiveQuiz, LiveQuizSubmission } from "./liveQuizTypes";
import { isLiveQuiz, setsEqual } from "./liveQuizTypes";
import {
  countConfidenceIndicators,
  quizScorePercent,
  QUIZ_CONFIDENCE_LABELS,
  QUIZ_CONFIDENCE_ORDER,
  QUIZ_CONFIDENCE_SHORT,
  resolveQuizConfidence,
  type QuizConfidenceIndicator,
} from "./quizConfidence";

function formatQuizTimer(remainingSec: number): string {
  const sec = Math.max(0, Math.floor(remainingSec));
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;
}

type TabId = "overview" | "questions" | "students";

type TrainerLiveQuizReportProps = {
  /** Panel is visible (docked in Meeting Tools). */
  active: boolean;
  quiz: ActiveLiveQuiz | null;
  /** All pushes in this session (newest first). */
  quizzes?: ActiveLiveQuiz[];
  submissions: LiveQuizSubmission[];
  /** Expected roster size (falls back to submitted count). */
  expectedCount?: number;
  /** @deprecated Grasp moved to Topic Absorption — accepted but unused */
  graspVotes?: unknown;
};

const CONFIDENCE_ICON: Record<
  QuizConfidenceIndicator,
  typeof Sun
> = {
  sun: Sun,
  moon: Moon,
  star: Star,
};

const CONFIDENCE_COLOR: Record<QuizConfidenceIndicator, string> = {
  sun: "#f59e0b",
  moon: "#94a3b8",
  star: "#fbbf24",
};

function ConfidenceIcon({
  kind,
  size = 14,
  showLabel,
}: {
  kind: QuizConfidenceIndicator;
  size?: number;
  showLabel?: boolean;
}) {
  const Icon = CONFIDENCE_ICON[kind];
  return (
    <span
      title={`${QUIZ_CONFIDENCE_SHORT[kind]} · ${QUIZ_CONFIDENCE_LABELS[kind]}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        color: CONFIDENCE_COLOR[kind],
      }}
    >
      <Icon size={size} fill={kind === "moon" ? "none" : CONFIDENCE_COLOR[kind]} />
      {showLabel ? (
        <span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
          {QUIZ_CONFIDENCE_SHORT[kind]}
        </span>
      ) : null}
    </span>
  );
}

export function TrainerLiveQuizReport({
  active,
  quiz,
  quizzes = [],
  submissions,
  expectedCount,
}: TrainerLiveQuizReportProps) {
  const [tab, setTab] = useState<TabId>("overview");
  // null = quiz list; set = detail for that push
  const [selectedPushId, setSelectedPushId] = useState<string | null>(null);
  // Students tab drill-down only (Questions tab shows option % inline).
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null,
  );
  const [remainingSec, setRemainingSec] = useState(0);
  /** Wall clock tick so list-page live timers stay current. */
  const [nowMs, setNowMs] = useState(() => Date.now());

  // ---------------------------------------------------------------
  // step 0a: Merge session quiz list + live active quiz
  // ---------------------------------------------------------------
  const quizList = useMemo(() => {
    const byId = new Map<string, ActiveLiveQuiz>();
    for (const q of quizzes) {
      if (q?.push_id) byId.set(q.push_id, q);
    }
    if (quiz?.push_id) {
      const existing = byId.get(quiz.push_id);
      byId.set(quiz.push_id, existing ? { ...existing, ...quiz } : quiz);
    }
    return Array.from(byId.values())
      .map((q) =>
        q.status === "active" && !isLiveQuiz(q, nowMs)
          ? { ...q, status: "completed" as const }
          : q,
      )
      .sort((a, b) => Number(b.pushed_at || 0) - Number(a.pushed_at || 0));
  }, [quizzes, quiz, nowMs]);

  const detailQuiz = useMemo(() => {
    if (!selectedPushId) return null;
    return (
      quizList.find((q) => q.push_id === selectedPushId) ||
      (quiz?.push_id === selectedPushId ? quiz : null)
    );
  }, [selectedPushId, quizList, quiz]);

  // Prefer live quiz detail when panel opens; otherwise start on list
  useEffect(() => {
    if (!active) {
      setSelectedPushId(null);
      setSelectedStudentId(null);
      setTab("overview");
      return;
    }
    if (isLiveQuiz(quiz) && quiz?.push_id) {
      setSelectedPushId(quiz.push_id);
    } else if (quizList.length === 1) {
      setSelectedPushId(quizList[0].push_id);
    } else {
      setSelectedPushId(null);
    }
    // Only re-pick when the panel opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ---------------------------------------------------------------
  // step 0b: Tick while panel open (list + detail live countdowns)
  // ---------------------------------------------------------------
  useEffect(() => {
    if (!active) return;
    const sync = () => setNowMs(Date.now());
    sync();
    const id = window.setInterval(sync, 1000);
    return () => window.clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (!active || !detailQuiz || !isLiveQuiz(detailQuiz, nowMs)) {
      setRemainingSec(0);
      return;
    }
    const elapsed = Math.floor(
      (nowMs - Number(detailQuiz.pushed_at || 0)) / 1000,
    );
    setRemainingSec(
      Math.max(0, Number(detailQuiz.duration_seconds || 0) - elapsed),
    );
  }, [
    active,
    nowMs,
    detailQuiz?.push_id,
    detailQuiz?.status,
    detailQuiz?.pushed_at,
    detailQuiz?.duration_seconds,
  ]);

  // ---------------------------------------------------------------
  // step 1: Filter submissions to selected push
  // ---------------------------------------------------------------
  const forPush = useMemo(
    () =>
      detailQuiz
        ? submissions.filter((s) => s.push_id === detailQuiz.push_id)
        : [],
    [detailQuiz, submissions],
  );

  const maxScore = detailQuiz?.questions.length ?? 0;
  const roster = Math.max(expectedCount || 0, forPush.length, 1);

  // ---------------------------------------------------------------
  // step 2: Overview aggregates
  // ---------------------------------------------------------------
  const avgPct = useMemo(() => {
    if (forPush.length === 0) return 0;
    const sum = forPush.reduce(
      (acc, s) => acc + (s.max_score > 0 ? s.score / s.max_score : 0),
      0,
    );
    return Math.round((sum / forPush.length) * 1000) / 10;
  }, [forPush]);

  const confidenceCounts = useMemo(
    () =>
      countConfidenceIndicators(
        forPush.map((s) => quizScorePercent(s.score, s.max_score)),
      ),
    [forPush],
  );

  const distribution = useMemo(() => {
    const buckets = Array.from({ length: maxScore + 1 }, (_, score) => ({
      score,
      count: 0,
      label:
        maxScore > 0
          ? `${score}/${maxScore} (${Math.round((score / maxScore) * 100)}%)`
          : `${score}`,
    }));
    for (const s of forPush) {
      const idx = Math.min(maxScore, Math.max(0, s.score));
      if (buckets[idx]) buckets[idx].count += 1;
    }
    return buckets.reverse();
  }, [forPush, maxScore]);

  const maxBucket = Math.max(1, ...distribution.map((b) => b.count));

  // ---------------------------------------------------------------
  // step 3: Per-question correct rates + option choice distribution
  // ---------------------------------------------------------------
  const questionStats = useMemo(() => {
    if (!detailQuiz) return [];
    const submitted = forPush.length;
    return detailQuiz.questions.map((q, qi) => {
      const optionCounts = q.options.map(() => 0);
      let correct = 0;
      let skipped = 0;
      const correctSet = q.correct_indices || [];
      for (const s of forPush) {
        const ans = s.answers[qi] || [];
        if (setsEqual(ans, correctSet)) correct += 1;
        if (ans.length === 0) {
          skipped += 1;
        } else {
          for (const oi of ans) {
            if (oi >= 0 && oi < optionCounts.length) {
              optionCounts[oi] += 1;
            }
          }
        }
      }
      const pct =
        submitted > 0 ? Math.round((correct / submitted) * 100) : 0;
      const options = q.options.map((label, oi) => {
        const count = optionCounts[oi] || 0;
        return {
          index: oi,
          label,
          count,
          percent: submitted > 0 ? Math.round((count / submitted) * 100) : 0,
          isCorrect: correctSet.includes(oi),
        };
      });
      return {
        questionIndex: qi,
        text: q.text,
        correctPercent: pct,
        options,
        skipped,
        skippedPercent:
          submitted > 0 ? Math.round((skipped / submitted) * 100) : 0,
      };
    });
  }, [detailQuiz, forPush]);

  if (!active) return null;

  const showingList = !selectedPushId;
  const isLive = isLiveQuiz(detailQuiz, nowMs);
  const slotLabel = detailQuiz?.slot === "pre" ? "Pre-Quiz" : "Post-Quiz";
  const timerEnded = !isLive || remainingSec <= 0;
  const timerUrgent = isLive && !timerEnded && remainingSec <= 30;
  const timerLabel = timerEnded ? "Ended" : formatQuizTimer(remainingSec);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 0,
        flex: 1,
      }}
    >
      {!showingList ? (
        <button
          type="button"
          onClick={() => {
            setSelectedPushId(null);
            setSelectedStudentId(null);
            setTab("overview");
          }}
          aria-label="Back to quiz list"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            alignSelf: "flex-start",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 8,
            background: "rgba(255,255,255,0.04)",
            color: "#e2e8f0",
            fontSize: 12,
            fontWeight: 600,
            padding: "6px 10px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>
            ←
          </span>
          All quizzes
        </button>
      ) : null}
          {showingList ? (
            quizList.length === 0 ? (
              <div
                style={{
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "#1a1d26",
                  padding: 24,
                  textAlign: "center",
                }}
              >
                <p style={{ margin: 0, fontWeight: 600, color: "#cbd5e1" }}>
                  No quizzes yet
                </p>
                <p style={{ margin: "8px 0 0", fontSize: 13, color: "#64748b" }}>
                  Push a pre- or post-quiz from Manage topic to see scores here.
                </p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                  {quizList.length} quiz{quizList.length === 1 ? "" : "zes"} this
                  session
                </p>
                {quizList.map((q) => {
                  const qSubs = submissions.filter((s) => s.push_id === q.push_id);
                  const live = isLiveQuiz(q, nowMs);
                  const slot = q.slot === "pre" ? "Pre-Quiz" : "Post-Quiz";
                  const listRemaining = live
                    ? Math.max(
                        0,
                        Number(q.duration_seconds || 0) -
                          Math.floor(
                            (nowMs - Number(q.pushed_at || 0)) / 1000,
                          ),
                      )
                    : 0;
                  const listUrgent = live && listRemaining <= 30;
                  const avg =
                    qSubs.length === 0
                      ? null
                      : Math.round(
                          (qSubs.reduce(
                            (acc, s) =>
                              acc + (s.max_score > 0 ? s.score / s.max_score : 0),
                            0,
                          ) /
                            qSubs.length) *
                            100,
                        );
                  return (
                    <button
                      key={q.push_id}
                      type="button"
                      onClick={() => {
                        setSelectedPushId(q.push_id);
                        setSelectedStudentId(null);
                        setTab("overview");
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        textAlign: "left",
                        borderRadius: 12,
                        border: live
                          ? "1px solid rgba(16,185,129,0.35)"
                          : "1px solid rgba(255,255,255,0.1)",
                        background: live
                          ? "rgba(16,185,129,0.1)"
                          : "rgba(255,255,255,0.04)",
                        padding: "14px 14px",
                        cursor: "pointer",
                        color: "inherit",
                      }}
                    >
                      <BarChart3
                        size={16}
                        color={live ? "#34d399" : "#94a3b8"}
                        style={{ flexShrink: 0 }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              color: live ? "#34d399" : "#94a3b8",
                            }}
                          >
                            {live ? "Live" : "Completed"} · {slot}
                          </span>
                          {live ? (
                            <span
                              aria-label={`Time remaining ${formatQuizTimer(listRemaining)}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 5,
                                borderRadius: 999,
                                padding: "3px 9px",
                                fontSize: 12,
                                fontWeight: 800,
                                fontVariantNumeric: "tabular-nums",
                                background: listUrgent
                                  ? "rgba(120, 53, 15, 0.55)"
                                  : "rgba(66, 66, 20, 0.65)",
                                color: listUrgent ? "#fbbf24" : "#facc15",
                                border: listUrgent
                                  ? "1px solid rgba(245, 158, 11, 0.55)"
                                  : "1px solid rgba(202, 138, 4, 0.55)",
                              }}
                            >
                              <Clock size={12} />
                              {formatQuizTimer(listRemaining)}
                            </span>
                          ) : null}
                        </div>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#fff",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {q.topic_title}
                        </p>
                        <p
                          style={{
                            margin: "4px 0 0",
                            fontSize: 12,
                            color: "#94a3b8",
                          }}
                        >
                          {qSubs.length} submitted
                          {avg != null ? ` · Avg ${avg}%` : ""}
                          {` · ${q.questions.length} Q`}
                        </p>
                      </div>
                      <ChevronRight
                        size={16}
                        color="#64748b"
                        style={{ flexShrink: 0 }}
                      />
                    </button>
                  );
                })}
              </div>
            )
          ) : !detailQuiz ? (
            <div
              style={{
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "#1a1d26",
                padding: 24,
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, color: "#cbd5e1" }}>
                Quiz not found
              </p>
              <button
                type="button"
                onClick={() => setSelectedPushId(null)}
                style={{
                  marginTop: 12,
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.04)",
                  color: "#e2e8f0",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 10px",
                  cursor: "pointer",
                }}
              >
                ← Back to quizzes
              </button>
            </div>
          ) : (
            <>
              {/* Live / completed header card */}
              <div
                style={{
                  borderRadius: 12,
                  border: isLive
                    ? "1px solid rgba(16,185,129,0.35)"
                    : "1px solid rgba(255,255,255,0.12)",
                  background: isLive
                    ? "rgba(16,185,129,0.1)"
                    : "rgba(255,255,255,0.04)",
                  padding: 14,
                }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <BarChart3
                    size={16}
                    color={isLive ? "#34d399" : "#94a3b8"}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: isLive ? "#34d399" : "#94a3b8",
                        }}
                      >
                        {isLive ? "Live quiz" : "Quiz complete"}
                      </p>
                      {detailQuiz ? (
                        <span
                          aria-label={
                            timerEnded
                              ? "Quiz timer ended"
                              : `Time remaining ${timerLabel}`
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            borderRadius: 999,
                            padding: "4px 10px",
                            fontSize: 13,
                            fontWeight: 800,
                            fontVariantNumeric: "tabular-nums",
                            background: timerEnded
                              ? "rgba(148,163,184,0.18)"
                              : timerUrgent
                                ? "rgba(245,158,11,0.2)"
                                : "rgba(37,99,235,0.25)",
                            color: timerEnded
                              ? "#94a3b8"
                              : timerUrgent
                                ? "#fbbf24"
                                : "#93c5fd",
                            border: timerEnded
                              ? "1px solid rgba(148,163,184,0.35)"
                              : timerUrgent
                                ? "1px solid rgba(245,158,11,0.45)"
                                : "1px solid rgba(59,130,246,0.4)",
                          }}
                        >
                          <Clock size={13} />
                          {timerLabel}
                        </span>
                      ) : null}
                    </div>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {slotLabel} · {detailQuiz.topic_title}
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>
                      {forPush.length}/{roster} submitted
                    </p>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "#1a1d26",
                  padding: 4,
                }}
              >
                {(["overview", "questions", "students"] as const).map((t) => {
                  const active = tab === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setTab(t);
                        setSelectedStudentId(null);
                      }}
                      style={{
                        flex: 1,
                        border: "none",
                        borderRadius: 8,
                        padding: "8px 6px",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "capitalize",
                        cursor: "pointer",
                        background: active ? "#2563eb" : "transparent",
                        color: active ? "#fff" : "#94a3b8",
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>

              {tab === "overview" ? (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                    }}
                  >
                    <div
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.1)",
                        background: "#1a1d26",
                        padding: 12,
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#34d399",
                        }}
                      >
                        {avgPct}%
                      </p>
                      <p style={{ margin: "4px 0 0", fontSize: 10, color: "#64748b" }}>
                        Avg score
                      </p>
                    </div>
                    <div
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.1)",
                        background: "#1a1d26",
                        padding: 12,
                        textAlign: "center",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 22,
                          fontWeight: 800,
                          color: "#fff",
                        }}
                      >
                        {forPush.length}/{roster}
                      </p>
                      <p style={{ margin: "4px 0 0", fontSize: 10, color: "#64748b" }}>
                        Submitted
                      </p>
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: "#64748b",
                        }}
                      >
                        Confidence indicators
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          fontSize: 10,
                          color: "#64748b",
                        }}
                      >
                        {QUIZ_CONFIDENCE_ORDER.map((k) => (
                          <span
                            key={k}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <ConfidenceIcon kind={k} size={12} />
                            {QUIZ_CONFIDENCE_LABELS[k]}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr",
                        gap: 8,
                      }}
                    >
                      {QUIZ_CONFIDENCE_ORDER.map((k) => (
                        <div
                          key={k}
                          style={{
                            borderRadius: 10,
                            border: "1px solid rgba(255,255,255,0.1)",
                            background: "#1a1d26",
                            padding: 10,
                            textAlign: "center",
                          }}
                        >
                          <p
                            style={{
                              margin: 0,
                              fontSize: 18,
                              fontWeight: 800,
                              color: "#e2e8f0",
                            }}
                          >
                            {confidenceCounts[k]}
                          </p>
                          <div
                            style={{
                              marginTop: 4,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <ConfidenceIcon kind={k} size={14} showLabel />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p
                      style={{
                        margin: "0 0 8px",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "#64748b",
                      }}
                    >
                      Score distribution
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {distribution.map((bucket) => (
                        <div
                          key={bucket.score}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 12,
                          }}
                        >
                          <span
                            style={{
                              width: 72,
                              flexShrink: 0,
                              color: "#94a3b8",
                              fontSize: 11,
                            }}
                          >
                            {bucket.label}
                          </span>
                          <div
                            style={{
                              flex: 1,
                              height: 8,
                              borderRadius: 999,
                              background: "rgba(255,255,255,0.1)",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${(bucket.count / maxBucket) * 100}%`,
                                borderRadius: 999,
                                background: "#10b981",
                              }}
                            />
                          </div>
                          <span
                            style={{
                              width: 24,
                              textAlign: "right",
                              fontWeight: 700,
                              color: "#cbd5e1",
                            }}
                          >
                            {bucket.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : null}

              {tab === "questions" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {questionStats.map((q) => (
                    <div
                      key={q.questionIndex}
                      style={{
                        borderRadius: 12,
                        border: "1px solid rgba(255,255,255,0.1)",
                        background: "#1a1d26",
                        padding: 12,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#fff",
                          lineHeight: 1.4,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        Q{q.questionIndex + 1}: {q.text}
                      </p>
                      <div
                        style={{
                          marginTop: 8,
                          height: 6,
                          borderRadius: 999,
                          background: "rgba(244,63,94,0.35)",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `${q.correctPercent}%`,
                            background: "#10b981",
                          }}
                        />
                      </div>
                      <p
                        style={{
                          margin: "6px 0 0",
                          fontSize: 10,
                          fontWeight: 600,
                          color: "#34d399",
                        }}
                      >
                        {q.correctPercent}% correct
                        {forPush.length > 0
                          ? ` · ${forPush.length} submitted`
                          : ""}
                      </p>

                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          flexDirection: "column",
                          gap: 8,
                        }}
                      >
                        {q.options.map((opt) => (
                          <div key={opt.index}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 8,
                                marginBottom: 4,
                              }}
                            >
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: 11,
                                  lineHeight: 1.35,
                                  color: opt.isCorrect ? "#6ee7b7" : "#cbd5e1",
                                  fontWeight: opt.isCorrect ? 700 : 500,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <span
                                  style={{
                                    display: "inline-block",
                                    width: 16,
                                    fontWeight: 700,
                                    color: opt.isCorrect ? "#34d399" : "#94a3b8",
                                  }}
                                >
                                  {String.fromCharCode(65 + opt.index)}.
                                </span>{" "}
                                {opt.label}
                                {opt.isCorrect ? (
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      fontSize: 9,
                                      fontWeight: 700,
                                      letterSpacing: "0.04em",
                                      textTransform: "uppercase",
                                      color: "#34d399",
                                    }}
                                  >
                                    Correct
                                  </span>
                                ) : null}
                              </p>
                              <span
                                style={{
                                  flexShrink: 0,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: opt.isCorrect ? "#34d399" : "#e2e8f0",
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {opt.percent}%
                                <span
                                  style={{
                                    marginLeft: 4,
                                    fontWeight: 500,
                                    color: "#64748b",
                                  }}
                                >
                                  ({opt.count})
                                </span>
                              </span>
                            </div>
                            <div
                              style={{
                                height: 5,
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.08)",
                                overflow: "hidden",
                              }}
                            >
                              <div
                                style={{
                                  height: "100%",
                                  width: `${opt.percent}%`,
                                  borderRadius: 999,
                                  background: opt.isCorrect
                                    ? "#10b981"
                                    : "#64748b",
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        {q.skipped > 0 ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 10,
                              color: "#64748b",
                            }}
                          >
                            Skipped / no answer: {q.skippedPercent}% ({q.skipped})
                          </p>
                        ) : null}
                        {forPush.length === 0 ? (
                          <p
                            style={{
                              margin: 0,
                              fontSize: 11,
                              color: "#64748b",
                            }}
                          >
                            No submissions yet
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {tab === "students" ? (
                selectedStudentId ? (
                  (() => {
                    const s = forPush.find(
                      (x) => x.student_id === selectedStudentId,
                    );
                    if (!s || !detailQuiz) return null;
                    const pct = quizScorePercent(s.score, s.max_score);
                    const kind = resolveQuizConfidence(pct);
                    return (
                      <div
                        style={{
                          borderRadius: 12,
                          border: "1px solid rgba(255,255,255,0.1)",
                          background: "#1a1d26",
                          padding: 14,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setSelectedStudentId(null)}
                          style={{
                            border: "none",
                            background: "transparent",
                            color: "#60a5fa",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            padding: 0,
                            marginBottom: 10,
                          }}
                        >
                          ← All students
                        </button>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#fff",
                          }}
                        >
                          {s.student_name}
                        </p>
                        <p
                          style={{
                            margin: "6px 0 0",
                            fontSize: 20,
                            fontWeight: 800,
                            color: "#34d399",
                          }}
                        >
                          {s.score}/{s.max_score} ({pct}%)
                        </p>
                        <div style={{ marginTop: 6 }}>
                          <ConfidenceIcon kind={kind} size={16} showLabel />
                        </div>
                        <ul
                          style={{
                            listStyle: "none",
                            margin: "12px 0 0",
                            padding: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          {detailQuiz.questions.map((q, qi) => {
                            const ans = s.answers[qi] || [];
                            const correct = setsEqual(
                              ans,
                              q.correct_indices || [],
                            );
                            const wrongLabel =
                              ans.length > 0
                                ? ans
                                    .filter(
                                      (i) => i >= 0 && i < q.options.length,
                                    )
                                    .map((i) => q.options[i])
                                    .join(", ") || "skipped"
                                : "skipped";
                            return (
                              <li
                                key={q.id || qi}
                                style={{
                                  borderRadius: 8,
                                  padding: "8px 10px",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: correct
                                    ? "rgba(16,185,129,0.12)"
                                    : "rgba(244,63,94,0.12)",
                                  color: correct ? "#6ee7b7" : "#fda4af",
                                }}
                              >
                                Q{qi + 1}:{" "}
                                {correct
                                  ? "Correct"
                                  : `Wrong · ${wrongLabel}`}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()
                ) : (
                  <div
                    style={{
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.1)",
                      overflow: "hidden",
                      maxHeight: 420,
                      overflowY: "auto",
                    }}
                  >
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: 12,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "#22262f",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                          }}
                        >
                          {["Student", "Score", "Indicator"].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "10px 12px",
                                textAlign: "left",
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: "0.06em",
                                textTransform: "uppercase",
                                color: "#64748b",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {forPush.length === 0 ? (
                          <tr>
                            <td
                              colSpan={3}
                              style={{
                                padding: 16,
                                color: "#64748b",
                                textAlign: "center",
                              }}
                            >
                              Waiting for submissions…
                            </td>
                          </tr>
                        ) : (
                          forPush.map((s) => {
                            const pct = quizScorePercent(s.score, s.max_score);
                            const kind = resolveQuizConfidence(pct);
                            return (
                              <tr
                                key={s.student_id}
                                style={{
                                  borderTop: "1px solid rgba(255,255,255,0.06)",
                                }}
                              >
                                <td style={{ padding: "10px 12px" }}>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedStudentId(s.student_id)
                                    }
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      color: "#93c5fd",
                                      fontWeight: 600,
                                      cursor: "pointer",
                                      padding: 0,
                                      fontSize: 12,
                                    }}
                                  >
                                    {s.student_name}
                                  </button>
                                </td>
                                <td
                                  style={{
                                    padding: "10px 12px",
                                    fontWeight: 700,
                                    color: "#34d399",
                                  }}
                                >
                                  {s.score}/{s.max_score} ({pct}%)
                                </td>
                                <td style={{ padding: "10px 12px" }}>
                                  <ConfidenceIcon kind={kind} size={14} />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )
              ) : null}
            </>
          )}
    </div>
  );
}
